import React, { useState, useRef } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const DocDropZoneField = ({
  fileTypes = ["*"],
  onUpload,
  uploadUrl,
  allowFolder = false,
  multiple = true,
  selectedFileLength = 0,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    processFiles(e.dataTransfer.files);
  };

  const handleFileChange = (event) => {
    processFiles(event.target.files);
  };

  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  const processFiles = (fileList) => {
    const filesArray = Array.from(fileList);
    let filteredFiles = filesArray;

    if (!fileTypes.includes("*")) {
      filteredFiles = filesArray.filter((file) =>
        fileTypes.some(
          (type) =>
            file.type.includes(type.replace("*", "")) ||
            file.name.endsWith(type.replace("*", ""))
        )
      );
    }

    if (onUpload) {
      onUpload(filteredFiles);
    }

    uploadFilesWithProgress(filteredFiles);
  };

  const uploadFilesWithProgress = (files) => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize === 0) {
      setUploadProgress(100);
      return;
    }

    let uploaded = 0;
    const progresses = new Map();

    files.forEach((file, index) => {
      const formData = new FormData();
      formData.append("file", file); // Adjust based on your API requirements

      const xhr = new XMLHttpRequest();
      progresses.set(index, 0);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const previousLoaded = progresses.get(index);
          const delta = event.loaded - previousLoaded;
          uploaded += delta;
          progresses.set(index, event.loaded);
          const progress = (uploaded / totalSize) * 100;
          setUploadProgress(progress);
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Handle successful upload
          if (onUpload) onUpload(files);
        }
      };

      xhr.onerror = () => {
        console.error("Upload failed");
        setUploadProgress(0);
      };

      xhr.open("POST", uploadUrl);
      xhr.send(formData);
    });
  };

  return (
    <Box
      onClick={handleBoxClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `1px dashed ${isDragActive ? "#1976d2" : "#6D6E6F"}`,
        borderRadius: "0.5rem",
        p: "1rem",
        mb: "1rem",
        cursor: "pointer",
        bgcolor: isDragActive ? "rgba(25, 118, 210, 0.1)" : "transparent",
        transition: "all 0.3s ease",
        textAlign: "center",
        width: "100%",
      }}
    >
      <UploadFileIcon
        sx={{
          fontSize: "2rem",
          color: isDragActive ? "#1976d2" : "#6D6E6F",
          transition: "color 0.3s ease",
        }}
      />
      <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
        {isDragActive
          ? "Drop files here"
          : allowFolder
          ? "Upload Files & Folders (Click or Drag & Drop)"
          : "Upload Files (Click or Drag & Drop)"}
      </Typography>

      <input
        type="file"
        ref={fileInputRef}
        webkitdirectory={allowFolder ? "true" : undefined}
        directory={allowFolder ? "true" : undefined}
        multiple={multiple}
        accept={fileTypes.join(",")}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        Total Files: {selectedFileLength} file(s) selected.
      </Typography>

      {uploadProgress > 0 && (
        <Box sx={{ width: "100%", mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption">
            {Math.round(uploadProgress)}% uploaded
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DocDropZoneField;
